import * as React from 'react';
import { useContext, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AreaShell from '../../chrome/AreaShell';
import OrgContext from '../../../Authentication/components/OrgContext';
import useOrgs from '../../../Orgs/components/hooks/useOrgs';
import { TCard, TEmpty, TFoot, Toolbar, TRow } from '../../components/table';
import { Dialog, Field, TextInput } from '../../components/forms';
import { ListState, useList, useToast } from '../../components/roster';
import './admin-orgs.css';

/*
 * Every organization on the platform (global admins). Open one for its
 * accounts; its licenses live on the Licenses tab filtered to it.
 */

export interface OrgRow {
    id: number;
    name: string;
    logo?: string | null;
}

/** Same as v2: set the client-side current org, then a clean reload, since every page's data is org-scoped. */
export function useSwitchIntoOrg() {
    const { org, setOrg } = useContext(OrgContext);
    return {
        currentId: (org?.id as number | undefined) ?? null,
        switchTo: (row: OrgRow) => {
            (setOrg as unknown as (o: OrgRow) => void)(row);
            window.location.assign('/org');
        },
    };
}

/** Create or rename an org. PUT sends only the name, as v2 does. */
export function OrgNameDialog({
    open,
    row,
    onClose,
    onSaved,
}: {
    open: boolean;
    /** null = create */
    row: OrgRow | null;
    onClose: () => void;
    onSaved: (name: string) => void;
}) {
    const { createOrg, updateOrg } = useOrgs();
    /* callers mount this per open (keyed), so the draft starts fresh */
    const [name, setName] = useState(row?.name ?? '');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const inFlight = useRef(false);
    const trimmed = name.trim();
    const unchanged = row !== null && trimmed === (row.name ?? '').trim();

    const submit = async () => {
        if (!trimmed || unchanged || inFlight.current) return;
        inFlight.current = true;
        setSaving(true);
        setError('');
        try {
            const ok = row
                ? await updateOrg(row.id, { name: trimmed })
                : await createOrg({ name: trimmed });
            if (!ok) throw new Error('empty');
            onSaved(trimmed);
            onClose();
        } catch {
            setError('Couldn’t save — try again.');
        } finally {
            inFlight.current = false;
            setSaving(false);
        }
    };

    return (
        <Dialog
            open={open}
            title={row ? `Rename ${row.name}` : 'Add organization'}
            onClose={() => !saving && onClose()}
            onSubmit={submit}
            footer={
                <>
                    <button type="button" className="tb-btn" onClick={onClose} disabled={saving}>
                        Cancel
                    </button>
                    <button type="submit" className="tb-new" disabled={saving || !trimmed || unchanged}>
                        {saving ? 'Saving…' : row ? 'Save' : 'Create organization'}
                    </button>
                </>
            }
        >
            <Field
                label="Name"
                help={row ? 'Everyone in this organization sees the new name.' : 'You can add accounts after it’s created.'}
                error={error || undefined}
            >
                <TextInput autoFocus value={name} maxLength={200} onChange={(e) => setName(e.target.value)} />
            </Field>
        </Dialog>
    );
}

export default function AdminOrgsV3() {
    const navigate = useNavigate();
    const { listOrgs } = useOrgs();
    const toast = useToast();
    const { currentId, switchTo } = useSwitchIntoOrg();
    const orgs = useList<OrgRow>(() =>
        listOrgs().then((data: OrgRow[]) =>
            [...(data ?? [])].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')),
        ),
    );
    const [query, setQuery] = useState('');
    const [dialog, setDialog] = useState<{ row: OrgRow | null } | null>(null);

    const rows = useMemo(() => orgs.rows ?? [], [orgs.rows]);
    const q = query.trim().toLowerCase();
    const visible = useMemo(
        () => rows.filter((r) => !q || (r.name ?? '').toLowerCase().includes(q) || String(r.id) === q.replace(/^#/, '')),
        [rows, q],
    );
    const openOrg = (r: OrgRow) => navigate(`/admin/orgs/${r.id}`);

    return (
        <AreaShell
            area="admin"
            tab="orgs"
            sub={orgs.rows ? `${rows.length} organization${rows.length === 1 ? '' : 's'}` : 'Every organization on the platform'}
            actions={
                <button className="tb-new" onClick={() => setDialog({ row: null })}>
                    Add organization
                </button>
            }
        >
            <Toolbar searchValue={query} onSearch={setQuery} searchPlaceholder="Search by name or #id" filterCount={0} />
            <ListState
                rows={orgs.rows}
                failed={orgs.failed}
                what="organizations"
                reload={orgs.reload}
                empty={rows.length === 0 ? 'No organizations yet.' : undefined}
                emptyAction="Add organization"
                onEmptyAction={() => setDialog({ row: null })}
            >
                {visible.length === 0 ? (
                    <TCard>
                        <TEmpty message={`Nothing matches “${query}”.`} actionLabel="Clear search" onAction={() => setQuery('')} />
                    </TCard>
                ) : (
                    <TCard>
                        {visible.map((r, i) => {
                            const current = r.id === currentId;
                            return (
                                <TRow
                                    key={r.id}
                                    index={i}
                                    name={r.name || `Organization #${r.id}`}
                                    badge={current ? 'Current' : undefined}
                                    meta={`#${r.id}`}
                                    date=""
                                    status=""
                                    href={`/admin/orgs/${r.id}`}
                                    onOpen={() => openOrg(r)}
                                    menuItems={[
                                        { label: 'Accounts', onClick: () => openOrg(r) },
                                        { label: 'Licenses', onClick: () => navigate(`/admin/licenses?org=${r.id}`) },
                                        { label: 'Rename…', onClick: () => setDialog({ row: r }) },
                                        ...(current ? [] : [{ label: 'Switch into this org', onClick: () => switchTo(r) }]),
                                    ]}
                                />
                            );
                        })}
                    </TCard>
                )}
                <TFoot>
                    {q ? `${visible.length} of ${rows.length} · filtered` : `${rows.length} organization${rows.length === 1 ? '' : 's'}`}
                    {' · switch into an org to edit its profile, white-label, and templates'}
                </TFoot>
            </ListState>

            {dialog && (
                <OrgNameDialog
                    key={dialog.row?.id ?? 'new'}
                    open
                    row={dialog.row}
                    onClose={() => setDialog(null)}
                    onSaved={(name) => {
                        toast.show(dialog.row ? `Renamed to ${name}.` : `Created ${name}.`);
                        orgs.reload();
                    }}
                />
            )}
            {toast.node}
        </AreaShell>
    );
}
